import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams, useHistory } from "react-router-dom";
import {
  addProduction,
  getExportProductById,
  getLatestProductionByProductId,
  getActiveMaterialsByBarcodeId,
} from "../../../services/Export";
import {
  Button,
  Chip,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Box,
  Divider,
  useTheme,
  alpha,
  CircularProgress,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import ProductionTableItem from "../../../components/ProductionTableItem";
import InputField from "../../../FormComponents/InputField";
import Ex_ProductionValidation from "../../../Validation/Ex_ProductionValidation";
import FormAlert from "../../../components/FormAlert";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import { getUser } from "../../../Utils/Common";
import { getCurrentProductCost } from "../../../services/ExportPrice";
import SubPersonSelectingPopup from "../../../components/SubPersonSelectingPopup";
import { getSubOrderPersonById } from "../../../services/SubOrderPerson";

const useStyles = makeStyles({
  container: {
    padding: "20px",
  },
});

function Ex_Production({ history }) {
  const theme = useTheme();
  const classes = useStyles();
  const historyRouter = useHistory();
  const { productId } = useParams();

  const [product, setProduct] = useState(null);
  const [bomMaterials, setBomMaterials] = useState([]);

  const [productionValues, setProductionValues] = useState({
    _productId: productId,
    _productionItems: [],
    _labourCost: 0.0,
    _electricityCost: 0.0,
    _transportCost: 0.0,
    _otherCost: 0.0,
    _packingCost: 0.0,
    _packingPersonId: null,
    _productionNoteNote: "",
    _productionQty: 0.0,
    _useStandardBOM: true,
    _bomVersion: null,
  });

  const [error, setError] = useState({
    _productionItems: "",
    _productionQty: "",
    _packingCost: "",
    _packingPerson: "",
  });

  // Packing person selection (reuses the Sub Order Person picker)
  const [openPackingPersonTable, setOpenPackingPersonTable] = useState(false);
  const [selectedPackingPersons, setSelectedPackingPersons] = useState([]);
  const [packingPersonData, setPackingPersonData] = useState(null);

  const handleOpenPackingPersonTable = () => {
    setOpenPackingPersonTable(true);
  };

  const handleClosePackingPersonTable = async () => {
    setOpenPackingPersonTable(false);
    if (selectedPackingPersons.length > 0) {
      let person = await getSubOrderPersonById(selectedPackingPersons[0]);
      if (person && person !== "Something went wrong...") {
        setPackingPersonData(person);
        handleChange("_packingPersonId", person.personId);
        setError((prev) => ({ ...prev, _packingPerson: "" }));
      }
    }
  };

  const clearPackingPerson = () => {
    setSelectedPackingPersons([]);
    setPackingPersonData(null);
    setProductionValues((prev) => ({
      ...prev,
      _packingPersonId: null,
      _packingCost: 0.0,
    }));
    setError((prev) => ({ ...prev, _packingCost: "", _packingPerson: "" }));
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Set product ID immediately
      handleChange("_productId", productId);

      let result = await getExportProductById(productId);
      console.log("Fetched product data:", result);
      if (result && result.product_id) {
        setProduct(result);

        // Fetch BOM materials from ProductBarcode
        if (result.barcodeId) {
          const materials = await getActiveMaterialsByBarcodeId(
            result.barcodeId,
          );

          if (materials && materials !== "Something went wrong...") {
            setBomMaterials(materials);

            // Create table rows from BOM materials
            const itemSet = materials.map((material) =>
              createDataForSelectedTable(
                material.materialName,
                material.materialBrand,
                material.materialUnitType,
                material.materialId,
                material.standardQtyPerUnit,
                null,
              ),
            );

            setRows(itemSet);

            // Set BOM version
            handleChange("_bomVersion", result.currentMaterialsVersion);
          }
        }
      }

      // Fetch current cost
      if (result.barcode) { 
        const costData = await getCurrentProductCost(result.barcode);
        if (costData && costData !== "Something went wrong...") {
          setProductionValues({
            ...productionValues,
            _electricityCost: costData.electricityCost || 0,
            _transportCost: costData.deliveryCharges || 0,
            _labourCost: costData.employeeCost || 0,
            _otherCost: costData.externalCost || 0,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    handleChange("_useStandardBOM", true);

    const fetchRecentRecord = async () => {
      let responseForRecord = await getLatestProductionByProductId(productId);
      setRecentrecord(responseForRecord);
    };

    fetchRecentRecord();
  }, [productId]);

  const [rows, setRows] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [recentRecord, setRecentrecord] = useState([]);

  // UPDATED: Removed unitPrice and unitPriceMethod columns
  const columns = [
    { id: "item", label: "Material Name", minWidth: 150, editable: false },
    { id: "brand", label: "Brand", minWidth: 100, editable: false },
    { id: "unitType", label: "Unit Type", minWidth: 100, editable: false },
    {
      id: "itemQty",
      label: "Qty per Unit",
      minWidth: 120,
      editable: false,
    },
    { id: "action", label: "Actions", minWidth: 100 },
  ];

  function createDataForSelectedTable(
    item,
    brand,
    unitType,
    importId,
    itemQty,
    avlQty,
  ) {
    return {
      item,
      brand,
      unitType,
      importId,
      itemQty,
      avlQty,
    };
  }

  const [page, setPage] = useState(0);
  const [enableSetting, setEnableSetting] = useState(false);
  const [editingRow, setEditingRow] = useState([]);
  const [shouldSave, setShouldSave] = useState(false);

  const [displayAlert, setAlert] = useState(false);
  const [alertData, setAlertData] = useState({
    type: "",
    message: "",
  });

  const closeAlert = () => {
    setAlert(false);
    setAlertData({
      type: "",
      message: "",
    });
  };

  const handleSaveClick = () => {
    // Build production items from rows
    // Note: unitPrice and unitPriceMethod will be fetched automatically by backend
    const productionItems = rows.map(({ importId, itemQty }) => ({
      importId: importId,
      usingQty: itemQty, // Qty per unit (will be multiplied by production qty in backend)
      bomStandardQty: itemQty, // Store the BOM standard for reference
      isManualOverride: false, // User can modify via settings
    }));

    handleChange("_productionItems", productionItems);
    setShouldSave(true);
  };

  const resetErros = () => {
    setError({
      _productionItems: "",
      _productionQty: "",
      _packingCost: "",
      _packingPerson: "",
    });
  };

  useEffect(() => {
    if (shouldSave) {
      resetErros();
      saveProductionValues();
      setShouldSave(false);
    }
  }, [productionValues._productionItems, shouldSave]);

  const handleAction = (event, row) => {
    switch (event) {
      case "settings":
        setEditingRow(row);
        setEnableSetting(true);
        break;
      default:
        return "foo";
    }
  };

  const handleChange = (name, val) => {
    if (name === "_productionQty") {
      setError({
        ...error,
        _productionQty: "",
      });
    }
    if (name === "_packingCost") {
      setError({
        ...error,
        _packingCost: "",
      });
    }
    setProductionValues({
      ...productionValues,
      [name]: val,
    });
  };

  const matchLastValues = () => {
    // Only match production quantity and costs from last record
    // Keep BOM quantities as they are
    setProductionValues({
      ...productionValues,
      _productionQty:
        recentRecord.productionQty != null ? recentRecord.productionQty : 0,
      _labourCost:
        recentRecord.labourCost != null ? recentRecord.labourCost : 0,
      _electricityCost:
        recentRecord.electricityCost != null ? recentRecord.electricityCost : 0,
      _transportCost:
        recentRecord.transportCost != null ? recentRecord.transportCost : 0,
      _otherCost: recentRecord.otherCost != null ? recentRecord.otherCost : 0,
      _packingCost:
        recentRecord.packingCost != null ? recentRecord.packingCost : 0,
      _packingPersonId: recentRecord.packingPerson
        ? recentRecord.packingPerson.personId
        : null,
      _productionNoteNote: recentRecord.note,
    });
    if (recentRecord.packingPerson) {
      setPackingPersonData(recentRecord.packingPerson);
      setSelectedPackingPersons([recentRecord.packingPerson.personId]);
    } else {
      setPackingPersonData(null);
      setSelectedPackingPersons([]);
    }
  };

  const clearData = () => {
    // Reset to BOM standard quantities
    let itemList = rows.map((row, index) => {
      const bomMaterial = bomMaterials[index];
      return {
        ...row,
        itemQty: bomMaterial ? bomMaterial.standardQtyPerUnit : 0,
      };
    });

    setRows(itemList);

    setProductionValues({
      ...productionValues,
      _productionQty: 0,
      _labourCost: 0.0,
      _electricityCost: 0.0,
      _transportCost: 0.0,
      _otherCost: 0.0,
      _packingCost: 0.0,
      _packingPersonId: null,
      _productionNoteNote: "",
    });
    setSelectedPackingPersons([]);
    setPackingPersonData(null);
  };

  const saveProductionValues = async () => {
    console.log("Production values to submit:", productionValues);
    console.log("Rows data:", rows);

    if (!productionValues._productId) {
      setAlertData({
        type: "error",
        message: "Product ID is missing. Please try again.",
      });
      setAlert(true);
      return;
    }

    let submitErrors = Ex_ProductionValidation(productionValues, rows);
    setError(submitErrors);

    if (
      !submitErrors._productionQty &&
      !submitErrors._productionItems &&
      !submitErrors._packingCost &&
      !submitErrors._packingPerson
    ) {
      const productionData = {
        ...productionValues,
        _productId: parseInt(productId),
        _addedBy: getUser().email,
      };

      setLoading(true);
      let result = await addProduction(productionData);
      setLoading(false);

      console.log("Production result:", result);

      if (result.success) {
        // Saved as a DRAFT — the approval flow continues on the details page,
        // where the user can send it for approval or delete it.
        const newProductionId = result.data?.productionId;
        if (newProductionId) {
          historyRouter.push(`/template/ex_production_view/${newProductionId}`);
          return;
        }
        clearData();
        setAlertData({
          type: "success",
          message: "Production saved as draft.",
        });
        setAlert(true);
      } else if (result.error === "INSUFFICIENT_STOCK") {
        // Format insufficient materials message
        if (
          result.insufficientMaterials &&
          result.insufficientMaterials.length > 0
        ) {
          const materialsList = result.insufficientMaterials
            .map(
              (m) =>
                `• ${m.materialName}: Required ${m.required.toFixed(2)}, Available ${m.available.toFixed(2)}, Short by ${m.shortage.toFixed(2)}`,
            )
            .join("\n");

          setAlertData({
            type: "error",
            message: `❌ Insufficient Material Stock:\n\n${materialsList}\n\nPlease restock materials before creating this production.`,
          });
        } else {
          setAlertData({
            type: "error",
            message:
              result.message || "Insufficient material stock for production",
          });
        }
        setAlert(true);

        setError({
          ...error,
          _productionItems:
            "⚠️ Insufficient material stock. Check details above.",
        });
      } else {
        setAlertData({
          type: "error",
          message:
            result.message || "Something went wrong while creating production.",
        });
        setAlert(true);
      }
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper className={classes.container} elevation={8}>
      {product && product.product_id ? (
        <Grid container spacing={3}>
          {/* Header */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h5" fontWeight="600">
                Production Entry
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => historyRouter.goBack()}
                >
                  Back
                </Button>
                <Button variant="contained" onClick={handleSaveClick}>
                  SAVE PRODUCTION
                </Button>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Product Info Card */}
          <Grid item xs={12} md={6}>
            <Card elevation={1}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="700" gutterBottom>
                  Product Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Product Name
                    </Typography>
                    <Typography variant="body1">{product.name}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Barcode
                    </Typography>
                    <Typography variant="body1">{product.barcode}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Current Stock
                    </Typography>
                    <Chip
                      label={product.existingQty}
                      size="small"
                      color={product.existingQty > 0 ? "success" : "warning"}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      BOM Version
                    </Typography>
                    <Chip
                      label={`v${product.currentMaterialsVersion || 1}`}
                      size="small"
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Production Quantity */}
          <Grid item xs={12} md={6}>
            <Card elevation={1}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="700" gutterBottom>
                  Production Details
                </Typography>
                <InputField
                  name="_productionQty"
                  value={productionValues._productionQty}
                  onChange={(event, newInputValue) =>
                    handleChange(event, parseInt(newInputValue))
                  }
                  errorMsg={error._productionQty}
                  type="number"
                  label="Production Quantity (Units)"
                  fullWidth
                />
              </CardContent>
            </Card>
          </Grid>

          {/* BOM Materials Table */}
          <Grid item xs={12}>
            <Card elevation={1}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="700">
                    Production Materials (BOM v
                    {product.currentMaterialsVersion || 1})
                  </Typography>
                  <Chip
                    label={`${bomMaterials.length} Materials`}
                    color="primary"
                    variant="outlined"
                  />
                </Box>

                {error._productionItems && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: "block", mb: 2, fontWeight: "bold" }}
                  >
                    {error._productionItems}
                  </Typography>
                )}

                <ProductionTableItem
                  columns={columns}
                  rows={rows}
                  setRows={setRows}
                  page={page}
                  setPage={setPage}
                  tablePagin={false}
                  getData={fetchData}
                  handleAction={handleAction}
                  showActions={{
                    settings: "Adjust quantity",
                  }}
                  actionRoles={{
                    settings: [
                      "PRODUCTION_CREATE",
                      "PRODUCTION_UPDATE",
                      "ROLE_ADMIN",
                    ],
                  }}
                  enableSetting={enableSetting}
                  setEnableSetting={setEnableSetting}
                  editingRow={editingRow}
                  setEditingRow={setEditingRow}
                />

                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    <strong>Note:</strong> Quantities shown are per unit from
                    BOM. Total material usage = Qty per unit × Production
                    quantity ({productionValues._productionQty} units)
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Production Costs */}
          <Grid item xs={12}>
            <Card elevation={1}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="700" gutterBottom>
                  Additional Production Costs (Optional)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <InputField
                      name="_electricityCost"
                      value={productionValues._electricityCost}
                      onChange={(event, newInputValue) =>
                        handleChange(event, parseFloat(newInputValue) || 0)
                      }
                      type="number"
                      label="Electricity Cost"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InputField
                      name="_labourCost"
                      value={productionValues._labourCost}
                      onChange={(event, newInputValue) =>
                        handleChange(event, parseFloat(newInputValue) || 0)
                      }
                      type="number"
                      label="Labour Cost"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InputField
                      name="_transportCost"
                      value={productionValues._transportCost}
                      onChange={(event, newInputValue) =>
                        handleChange(event, parseFloat(newInputValue) || 0)
                      }
                      type="number"
                      label="Transport Cost"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InputField
                      name="_otherCost"
                      value={productionValues._otherCost}
                      onChange={(event, newInputValue) =>
                        handleChange(event, parseFloat(newInputValue) || 0)
                      }
                      type="number"
                      label="Other Cost"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      Packaging (Optional)
                    </Typography>
                    {packingPersonData ? (
                      <Chip
                        icon={<PersonIcon />}
                        label={`${packingPersonData.personName} (${packingPersonData.phone})`}
                        onDelete={clearPackingPerson}
                        color="primary"
                        variant="outlined"
                        sx={{
                          width: "100%",
                          height: 40,
                          justifyContent: "flex-start",
                          "& .MuiChip-label": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        }}
                      />
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PersonIcon />}
                        onClick={handleOpenPackingPersonTable}
                        fullWidth
                        sx={{
                          height: 40,
                          justifyContent: "flex-start",
                          textTransform: "none",
                        }}
                      >
                        Select Packaging Person
                      </Button>
                    )}
                    {error._packingPerson && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        {error._packingPerson}
                      </Typography>
                    )}
                  </Grid>
                  {packingPersonData && (
                    <Grid item xs={12} sm={6} md={3}>
                      <InputField
                        name="_packingCost"
                        value={productionValues._packingCost}
                        onChange={(event, newInputValue) =>
                          handleChange(event, parseFloat(newInputValue) || 0)
                        }
                        errorMsg={error._packingCost}
                        type="number"
                        label="Packaging Cost"
                        fullWidth
                      />
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Alert */}
          {displayAlert && (
            <Grid item xs={12}>
              <Card
                sx={{
                  bgcolor:
                    alertData.type === "error"
                      ? alpha(theme.palette.error.main, 0.1)
                      : alpha(theme.palette.success.main, 0.1),
                  borderLeft: `4px solid ${
                    alertData.type === "error"
                      ? theme.palette.error.main
                      : theme.palette.success.main
                  }`,
                }}
              >
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    fontWeight="600"
                    color={
                      alertData.type === "error" ? "error.main" : "success.main"
                    }
                  >
                    {alertData.type === "error" ? "Error" : "Success"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-line", mt: 1 }}
                  >
                    {alertData.message}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {openPackingPersonTable ? (
            <SubPersonSelectingPopup
              setOpenPersonTable={setOpenPackingPersonTable}
              openPersonTable={openPackingPersonTable}
              handleClosePersonTable={handleClosePackingPersonTable}
              setSelectedPerson={setSelectedPackingPersons}
              selectedPerson={selectedPackingPersons}
              isOneChoise={true}
            />
          ) : null}
        </Grid>
      ) : (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ minHeight: "400px" }}
        >
          <CircularProgress />
        </Grid>
      )}
    </Paper>
  );
}

Ex_Production.propTypes = {
  product_id: PropTypes.any,
};

export default Ex_Production;
